/* Form-family park: the cell editor converts with the Table round (component-map.md). */
import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Popover as ChakraPopover,
  PopoverContent as ChakraPopoverContent,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { Text } from '@main/complykit/components/text';
import { Nullable } from '@main/shared/types';
import { FilterMode, isIsEmptyOrIsNotEmpty } from '@main/shared/url-helpers';
import { dateToMidnightUTC, formatDate, MAX_DATE_VALUE } from '@main/shared/utils';
import { isEmpty } from '@main/shared/utils/is-empty';
import { CellContext, ColumnDef, Row, RowData, SortDirection } from '@tanstack/react-table';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';
import {
  FocusEventHandler,
  ForwardedRef,
  forwardRef,
  LegacyRef,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { rangeToDates } from '../../../utils';
import { EditablePopover } from '../../editable/editable-popover';
import { OverflowContainer } from '../../overflow-tooltip';
import { errorToast } from '../../toast';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../bulk-actions';
import { createFilterHelper } from '../filters';
import { FilterLabelRow } from '../filters/filter-label-row';
import { FilterModeSelector } from '../filters/filter-mode';
import { CellContainer } from '../shared/cell';
import { CanEditGuard, getEditable, ifBulkEditable } from '../table';
import {
  bulkScalarClearAction,
  BulkScalarEditAction,
  ScalarBulkClearButton,
} from './scalar-bulk-clear';

type EditProps<TData> = {
  onChange: (row: Row<TData>, value: string | null) => void;
  canEditGuard?: CanEditGuard<TData, DateCellValue>;
  min?: string;
  max?: string;
};

export type BulkDateOnChange<TData> = (
  rows: Row<TData>[],
  value: string | null,
  action?: BulkScalarEditAction,
) => unknown;
export type UseBulkDateChangeHook<TData> = () => readonly [BulkDateOnChange<TData>, boolean];

type DateColumnConfig<TData, TFilter, TSort> = {
  id: string;
  header: string;
  dateFormat?: string;
  displayMode?: 'absolute' | 'relative';
  emptyLabel?: string;
  edit?: EditProps<TData>;
  bulkEdit?: {
    useBulkChange: UseBulkDateChangeHook<TData>;
    allowClearValue?: boolean;
    bulkInitialValue?: string | undefined;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
  getColumnFilterCondition?(
    filterMode: FilterMode,
    filterValue: (string | null)[],
  ): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
};

type DateColumnDef<TData extends RowData, TValue, TFilter, TSort> = ColumnDef<TData, TValue> &
  DateColumnConfig<TData, TFilter, TSort>;

type DateCellValue = string | null | undefined;
type DateCellRenderer<TData> = (context: CellContext<TData, DateCellValue>) => JSX.Element;

const filterHelper = createFilterHelper({
  filterSchema: z.array(z.string().nullable()),
  filterFn,
});

const cachedFilterState = {
  hash: '',
  firstTime: 0,
  secondTime: 0,
  updateCache(filterValue: (string | null)[], filterMode?: FilterMode) {
    const hash = this.getHash(filterValue, filterMode);

    if (hash === this.hash) {
      return;
    }

    const [first, second] = filterValue;

    if (!first) {
      return;
    }

    const [firstDate, secondDate] = rangeToDates(first, second);

    this.firstTime = firstDate.getTime();
    this.secondTime = secondDate.getTime();
    this.hash = hash;
  },
  getHash(filterValue: (string | null)[], filterMode?: FilterMode) {
    return `${filterValue?.[0]}:${filterValue?.[1]}:${filterMode}`;
  },
};

function filterFn(
  value: string | undefined | null,
  filterValue: (string | null)[] | null,
  filterMode?: FilterMode,
): boolean {
  if (filterMode === FilterMode.IsEmpty) {
    return isEmpty(value);
  }

  if (filterMode === FilterMode.IsNotEmpty) {
    return !isEmpty(value);
  }

  if (!filterValue?.length || !filterValue[0]) {
    return true;
  }

  if (!value) {
    return false;
  }

  const date = new Date(value);
  cachedFilterState.updateCache(filterValue, filterMode);

  if (!filterMode || filterMode === FilterMode.Includes) {
    return (
      date.getTime() >= cachedFilterState.firstTime &&
      date.getTime() <= cachedFilterState.secondTime
    );
  }

  if (filterMode === FilterMode.Before) {
    return date.getTime() <= cachedFilterState.secondTime;
  }

  if (filterMode === FilterMode.After) {
    return date.getTime() >= cachedFilterState.firstTime;
  }

  if (filterMode === FilterMode.Between && filterValue[1]) {
    return (
      date.getTime() >= cachedFilterState.firstTime &&
      date.getTime() <= cachedFilterState.secondTime
    );
  }

  return true;
}

const anyValueChanged = <
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  T extends unknown,
>(
  rows: Row<T>[],
  columnId: string,
  newValue: string | undefined,
): boolean =>
  rows.some((row) => {
    const value = row.getValue(columnId);
    const bothFalsy = !value && !newValue;
    return !bothFalsy && value !== newValue;
  });

export function date<TData, TFilter, TSort>() {
  return ({
    dateFormat,
    displayMode = 'absolute',
    emptyLabel,
    edit,
    bulkEdit,
    ...columnDef
  }: DateColumnDef<TData, string | undefined | null, TFilter, TSort>): ColumnDef<
    TData,
    string | undefined | null
  > => {
    const editIcon =
      !!columnDef?.meta?.cell?.onClick || !!columnDef?.meta?.cell?.forceEditViaFloatingButton;

    const useBulkChange = bulkEdit?.useBulkChange;

    const getBulkInitialValue = <
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
      T extends unknown,
    >(
      rows: Row<T>[],
      columnId: string,
    ): string | undefined => {
      const [firstRow, ...restRows] = rows;
      if (!firstRow) return undefined;

      const value = firstRow.getValue<string>(columnId);
      return restRows.every((row) => equal(row.getValue(columnId), value)) ? value : undefined;
    };

    const editable = getEditable(edit);

    return {
      enableSorting: false,
      sortDescFirst: false,
      sortingFn: (rowA, rowB, columnId) => {
        //eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const valueA = rowA.getValue(columnId) as string | undefined;
        //eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const valueB = rowB.getValue(columnId) as string | undefined;

        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;

        return dayjs(valueA).isBefore(dayjs(valueB)) ? -1 : 1;
      },
      enableColumnFilter: false,
      ...columnDef,
      enableGlobalFilter: false,

      cell: (context) => {
        const cellRender =
          columnDef.cell && typeof columnDef.cell === 'function' ? columnDef.cell : undefined;
        if (edit && editable(context.row.original, context)) {
          return (
            <EditableCell
              context={context}
              editIcon={editIcon}
              render={cellRender}
              dateFormat={dateFormat}
              displayMode={displayMode}
              emptyLabel={emptyLabel}
              {...edit}
            />
          );
        }

        const value = context.getValue();
        const formattedValue = formatDateValue(value, dateFormat, displayMode, emptyLabel);
        const tooltipLabel = formatDateValue(value, dateFormat, displayMode, emptyLabel, true);
        const renderedDate = (
          <RenderDate formattedValue={formattedValue} context={context} render={cellRender} />
        );

        return (
          <CellContainer cell={context.cell} p={0}>
            {cellRender ? (
              renderedDate
            ) : displayMode === 'relative' && value ? (
              <Tooltip
                label={tooltipLabel}
                hasArrow
                placement="bottom-start"
                fontSize="sm"
                openDelay={500}
              >
                {renderedDate}
              </Tooltip>
            ) : (
              <OverflowContainer>
                <OverflowContainer.Tooltip
                  label={tooltipLabel}
                  hasArrow
                  placement="bottom-start"
                  fontSize="sm"
                  openDelay={500}
                >
                  {renderedDate}
                </OverflowContainer.Tooltip>
              </OverflowContainer>
            )}
          </CellContainer>
        );
      },

      filterFn: filterHelper.filterFn,

      meta: {
        ...columnDef.meta,
        columnType: 'date',
        cell: {
          fontSize: 'xs',
          color: 'gray.500',
          ...columnDef.meta?.cell,
        },
        name: columnDef.header,
        getColumnFilterCondition: columnDef.getColumnFilterCondition as never,
        getColumnSort: columnDef.getColumnSort as never,
        editable,
        bulkEdit: ifBulkEditable(
          edit,
          bulkEdit,
          () =>
            bulkEdit &&
            useBulkChange && {
              ...bulkEdit,
              id: columnDef.id,
              editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => {
                const defaultValue = getBulkInitialValue(editorRows, columnDef.id);
                const rowIds = editorRows.map((row) => row.id).join('');
                return (
                  <BulkDateEditor
                    id={columnDef.id}
                    key={`${columnDef.id}-${rowIds}`}
                    trigger={trigger}
                    isInMenu={isInMenu}
                    defaultValue={defaultValue}
                    rows={editorRows}
                    useBulkChange={useBulkChange}
                    allowClearValue={bulkEdit.allowClearValue}
                    onOpenChange={onOpenChange}
                    labelledBy={labelledBy}
                  />
                );
              },
            },
        ),
        filter: {
          filterSchema: filterHelper.filterSchema,

          DisplayFilter: ({ column }) => {
            const { t } = useTranslation('ui');
            const mode = filterHelper.getFilterMode(column);
            const value = filterHelper.getFilterValue(column);
            const firstValue = value?.[0];
            const secondValue = value?.[1];

            return (
              <span>
                {mode === FilterMode.IsEmpty || mode === FilterMode.IsNotEmpty ? (
                  mode === FilterMode.IsEmpty ? (
                    t('table.filter.isEmpty')
                  ) : (
                    t('table.filter.isNotEmpty')
                  )
                ) : (
                  <>
                    {mode === FilterMode.Before && `${t('table.filter.before')} `}
                    {mode === FilterMode.After && `${t('table.filter.after')} `}
                    {formatDate(firstValue)}
                    {mode === FilterMode.Between && ` - ${formatDate(secondValue)}`}
                  </>
                )}
              </span>
            );
          },

          EditFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const modeOptions = useMemo(
              () => [
                { label: t('table.filter.is'), value: FilterMode.Includes },
                { label: t('table.filter.after'), value: FilterMode.After },
                { label: t('table.filter.before'), value: FilterMode.Before },
                { label: t('table.filter.between'), value: FilterMode.Between },
                { label: t('table.filter.isEmpty'), value: FilterMode.IsEmpty },
                { label: t('table.filter.isNotEmpty'), value: FilterMode.IsNotEmpty },
              ],
              [t],
            );

            const mode = filterHelper.getFilterMode(column);
            const value = filterHelper.getFilterValue(column);
            const defaultStartDate = value?.[0] ?? null;
            const defaultEndDate = value?.[1] ?? null;
            const firstLabel = useMemo(
              () =>
                mode === FilterMode.Includes
                  ? t('table.filter.is')
                  : mode === FilterMode.After
                    ? t('table.filter.after')
                    : mode === FilterMode.Before
                      ? t('table.filter.before')
                      : t('table.filter.after'),
              [mode, t],
            );

            const hasInput = !isIsEmptyOrIsNotEmpty(mode);

            return (
              <FormControl p={3}>
                <FormLabel fontWeight="medium" mb={hasInput ? undefined : 0}>
                  <FilterLabelRow label={columnDef.header}>
                    <FilterModeSelector {...filterHelper} column={column} options={modeOptions} />
                  </FilterLabelRow>
                </FormLabel>
                {hasInput && (
                  <HStack>
                    <Input
                      autoFocus
                      size="sm"
                      placeholder={t('table.column.date.filterPlacehoder')}
                      borderRadius="base"
                      type="date"
                      defaultValue={defaultStartDate ?? ''}
                      max={
                        mode === FilterMode.Between && defaultEndDate
                          ? defaultEndDate
                          : MAX_DATE_VALUE
                      }
                      aria-label={`${columnDef.header} ${firstLabel}`}
                      onChange={(event) => {
                        const value = event.target.value;
                        const startDate =
                          !defaultEndDate || dayjs(value).isSameOrBefore(dayjs(defaultEndDate))
                            ? value
                            : null;

                        filterHelper.setFilterValue(column, [startDate, defaultEndDate]);
                      }}
                    />

                    {mode === FilterMode.Between && (
                      <Input
                        autoFocus
                        size="sm"
                        placeholder={t('table.column.date.filterPlacehoder')}
                        borderRadius="base"
                        type="date"
                        defaultValue={defaultEndDate ?? ''}
                        min={defaultStartDate ?? undefined}
                        max={MAX_DATE_VALUE}
                        aria-label={`${columnDef.header} ${t('table.filter.before')}`}
                        onChange={(event) => {
                          const value = event.target.value;
                          const endDate = dayjs(value).isBefore(dayjs(defaultStartDate))
                            ? null
                            : value;

                          filterHelper.setFilterValue(column, [defaultStartDate, endDate]);
                        }}
                      />
                    )}
                  </HStack>
                )}
              </FormControl>
            );
          },
        },
      },
    };
  };
}

type EditableCellProps<TData> = EditProps<TData> & {
  context: CellContext<TData, DateCellValue>;
  dateFormat?: string;
  displayMode?: 'absolute' | 'relative';
  emptyLabel?: string;
  editIcon?: boolean;
  render?: DateCellRenderer<TData>;
};

function EditableCell<TData>({
  context,
  onChange,
  dateFormat,
  displayMode,
  emptyLabel,
  editIcon,
  render,
  ...editProps
}: EditableCellProps<TData>) {
  const defaultValue = context.getValue() ?? '';
  const formattedDefaultValue = formatDateValue(defaultValue, dateFormat, displayMode, emptyLabel);

  const datepickerBgColor = useColorModeValue('gray.25', 'gray.700');

  /* Removing border width to get the exact contend width */
  const size = context.column.columnDef.size ? context.column.columnDef.size - 3 : 160;

  const onCellUpdate = () => {
    const newValue = dateToMidnightUTC(initialFocusRef.current?.value);
    let currentValue = '';
    try {
      currentValue = defaultValue ? new Date(defaultValue).toISOString() : '';
    } catch {
      datadogLogs.logger.error('Corrupted date value in DB', { defaultValue });
    }

    if (newValue !== currentValue) {
      onChange(context.row, newValue || null);
    }
  };

  const initialFocusRef = useRef<HTMLInputElement>(null);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const renderedDate = (
    <RenderDate
      formattedValue={formattedDefaultValue}
      context={context}
      render={render}
      isFullWidth
    />
  );

  return (
    <>
      <OverflowContainer
        data-stop-row-click="true"
        justify="space-between"
        onClick={(event) => {
          if (editIcon) return;
          event.stopPropagation();
          onToggle();
        }}
      >
        {render ? (
          renderedDate
        ) : (
          <OverflowContainer.Tooltip
            label={formattedDefaultValue}
            placement="bottom-start"
            fontSize="sm"
            openDelay={500}
          >
            {renderedDate}
          </OverflowContainer.Tooltip>
        )}
        {editIcon && (
          <OverflowContainer.EditTrigger
            tooltip={context.cell.column.columnDef.meta?.cell?.editTriggerTooltip}
            onClick={onToggle}
          />
        )}
      </OverflowContainer>

      {/* Parked with the Table round (popover.md): a cell's editor is the table's contract. */}
      <ChakraPopover isLazy initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onClose}>
        <ChakraPopoverContent
          maxW="fit-content"
          rounded="none"
          bgColor={datepickerBgColor}
          _focusVisible={{
            outline: 'none',
          }}
        >
          <DateEditor
            {...editProps}
            ref={initialFocusRef}
            defaultValue={defaultValue}
            onBlur={onCellUpdate}
            width={size}
          />
        </ChakraPopoverContent>
      </ChakraPopover>
    </>
  );
}

type BulkDateEditorProps<TData> = Omit<EditProps<TData>, 'onChange' | 'canEditGuard'> & {
  id: string;
  isInMenu: boolean;
  trigger: JSX.Element;
  defaultValue: string | undefined;
  rows: Row<TData>[];
  useBulkChange: UseBulkDateChangeHook<TData>;
  allowClearValue?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  labelledBy?: string;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const BulkDateEditor = <TData extends unknown>({
  id,
  isInMenu,
  trigger,
  defaultValue,
  rows,
  useBulkChange,
  allowClearValue = false,
  onOpenChange,
  labelledBy,
  ...editProps
}: BulkDateEditorProps<TData>) => {
  const { t } = useTranslation('ui');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const skipNextUpdateRef = useRef(false);
  const [onChange, isLoading] = useBulkChange();
  useSyncIsBulkLoading(id, isLoading);
  useEffect(() => {
    skipNextUpdateRef.current = false;
  }, [defaultValue]);

  const onUpdate = async () => {
    if (skipNextUpdateRef.current) {
      skipNextUpdateRef.current = false;
      return;
    }

    const input = inputRef.current;
    const inputValue = input?.value ?? '';

    if (input && !input.validity.valid) {
      errorToast(t('table.invalidValue'));
      return;
    }

    if (allowClearValue && !inputValue) {
      return;
    }

    const newValue = dateToMidnightUTC(inputValue);
    if (inputValue && !newValue) {
      errorToast(t('table.invalidValue'));
      return;
    }

    const currentValue = dateToMidnightUTC(defaultValue);

    if (anyValueChanged(rows, id ?? '', newValue) && newValue !== currentValue) {
      const submittedValue = newValue || null;
      await onChange(rows, submittedValue);
    }
  };

  const handleClear = async (setOpen: (isOpen: boolean) => void) => {
    if (isLoading) {
      return;
    }

    const currentValue = dateToMidnightUTC(inputRef.current?.value ?? defaultValue ?? '');
    const previousValue = inputRef.current?.value;
    skipNextUpdateRef.current = true;
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    const didUpdate = await onChange(rows, currentValue, bulkScalarClearAction);
    if (didUpdate !== false) {
      skipNextUpdateRef.current = true;
      setOpen(false);
      return;
    }

    skipNextUpdateRef.current = false;
    if (inputRef.current && previousValue !== undefined) {
      inputRef.current.value = previousValue;
    }
  };

  return (
    <EditablePopover
      onClose={onUpdate}
      onOpenChange={onOpenChange}
      openEvent={isInMenu ? 'mousedown' : undefined}
      placement={isInMenu ? 'left-start' : 'top-end'}
      closeOnBlur={false}
    >
      <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
      <EditablePopover.Content
        borderRadius="sm"
        outline="2px solid"
        outlineColor="chakra-border-color"
        minWidth="xs"
        offset={-1}
        data-testid={`bulk-editor-${id}`}
      >
        {(context) => {
          const editor = (
            <DateEditor
              {...editProps}
              defaultValue={defaultValue ?? ''}
              size="xs"
              ref={(el) => {
                inputRef.current = el;
              }}
              labelledBy={labelledBy}
              hasClearButton={allowClearValue}
              onBlur={allowClearValue ? undefined : () => context.setOpen(false)}
              onEnter={() => context.setOpen(false)}
              onEscape={() => context.setOpen(false)}
            />
          );

          return allowClearValue ? (
            <Box position="relative">
              {editor}
              <ScalarBulkClearButton
                isDisabled={isLoading}
                onClick={() => void handleClear(context.setOpen)}
              />
            </Box>
          ) : (
            editor
          );
        }}
      </EditablePopover.Content>
    </EditablePopover>
  );
};

type DateEditorProps<TData> = Omit<EditProps<TData>, 'onChange' | 'canEditGuard'> & {
  defaultValue: string;
  width?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onBlur?: FocusEventHandler<HTMLInputElement>;
  labelledBy?: string;
  hasClearButton?: boolean;
  onEnter?: () => void;
  onEscape?: () => void;
};

const DateEditor = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  <TData extends unknown>(
    {
      width,
      size,
      min,
      max,
      defaultValue,
      onBlur,
      labelledBy,
      hasClearButton,
      onEnter,
      onEscape,
    }: DateEditorProps<TData>,
    ref: LegacyRef<HTMLInputElement> | undefined,
  ) => {
    const datepickerBgColor = useColorModeValue('gray.25', 'gray.700');

    return (
      <Input
        ref={ref}
        bg={datepickerBgColor}
        type="date"
        data-testid="date-input"
        fontSize="none"
        h="50px"
        w={width ? `${width}px` : undefined}
        border={0}
        pr={hasClearButton ? 10 : undefined}
        outline="none"
        focusBorderColor="transparent"
        rounded="sm"
        size={size}
        min={min}
        max={max ?? MAX_DATE_VALUE}
        defaultValue={dayjs(defaultValue).format('YYYY-MM-DD')}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onEscape?.();
            return;
          }

          if (e.key === 'Enter') {
            if (onEnter) {
              e.preventDefault();
              e.stopPropagation();
              onEnter();
              return;
            }

            e.currentTarget.blur();
          }
        }}
        /* This stops triggering click event on cell itself when clicking on the datepicker */
        onClick={(event) => event.stopPropagation()}
        autoFocus
        aria-labelledby={labelledBy}
      />
    );
  },
);

interface RenderDateProps<TData> {
  formattedValue: string;
  context: CellContext<TData, DateCellValue>;
  render?: DateCellRenderer<TData>;
  /** Overrides the cell's own inset, for a row that sets its own. */
  isFullWidth?: boolean;
}

const RenderDate = forwardRef(RenderDateInner) as <TData>(
  props: RenderDateProps<TData> & { ref?: ForwardedRef<HTMLDivElement> },
) => JSX.Element;

function RenderDateInner<TData>(
  { formattedValue, context, render, isFullWidth = false, ...props }: RenderDateProps<TData>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return render ? (
    render(context)
  ) : (
    // The wrapping tooltip clones its trigger handlers in as props; without them it never opens.
    <Box py={4} px={2} w={isFullWidth ? 'full' : undefined} ref={ref} {...props}>
      {/* No size: a cell reads at whatever the table sets, so every column
          matches without each one naming a step. */}
      <Text size="inherit" isTruncated>
        {formattedValue}
      </Text>
    </Box>
  );
}

function formatDateValue(
  value?: string | Date | null,
  format?: string,
  displayMode: 'absolute' | 'relative' = 'absolute',
  emptyLabel?: string,
  tooltip = false,
) {
  if (!value) return emptyLabel ?? '';
  // if the input is not a valid date, then it is used to inform the user why
  // the date is not possible to display (e.g. "Confidential" evidence)
  if (isNaN(new Date(value).getTime())) return String(value);

  if (displayMode === 'relative') {
    return tooltip ? dayjs(value).format('LLL') : dayjs(value).fromNow();
  }

  return formatDate(value, format);
}
